import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { MixerHorizontalIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ALL_ORDER_TYPES } from "@/constants";
import { OrderFilterState } from "@/models";

interface OrderFilterDrawerProps {
  filterState: [
    OrderFilterState,
    React.Dispatch<React.SetStateAction<OrderFilterState>>,
  ];
}

const types = [
  {
    id: "send",
    label: "Send",
  },
  {
    id: "receive",
    label: "Receive",
  },
] as const;

const FormSchema = z.object({
  types: z.array(z.string()),
});

const OrdersFilterDrawer = ({ filterState }: OrderFilterDrawerProps) => {
  const [filters, setFilters] = filterState;

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      types: filters.types == ALL_ORDER_TYPES ? [] : filters.types,
    },
  });

  const setAdjustedFilters = (filterValues: OrderFilterState) => {
    const newState: OrderFilterState = {
      types: [],
    };
    // TYPES ADJUSTMENT
    newState.types =
      filterValues.types.length === 0 ? ALL_ORDER_TYPES : filterValues.types;
    setFilters(newState);
  };

  return (
    <Drawer>
      <DrawerTrigger className="flex items-center justify-center gap-2">
        <MixerHorizontalIcon className="h-5 w-5 text-text-primary" />
        <p className="capitalize leading-5 text-text-primary">Filters</p>
      </DrawerTrigger>
      <Form {...form}>
        <form className="space-y-8">
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Orders</DrawerTitle>
              <DrawerDescription>Apply Filters</DrawerDescription>
            </DrawerHeader>
            {/* DRAWER_BODY */}
            <section className="mx-4 flex flex-col justify-center gap-2">
              <FormField
                control={form.control}
                name="types"
                render={() => (
                  <FormItem className="mb-4">
                    <div className="mb-2">
                      <FormLabel className="text-base text-text-primary">
                        Type
                      </FormLabel>
                      <FormDescription>
                        Select the order types to display
                      </FormDescription>
                    </div>
                    <div className="flex items-center justify-start gap-6">
                      {types.map((item) => (
                        <FormField
                          key={item.id}
                          control={form.control}
                          name="types"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={item.id}
                                className="flex flex-row items-center space-x-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(item.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([
                                            ...field.value,
                                            item.id,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== item.id,
                                            ),
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal text-text-light">
                                  {item.label}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>
            {/* DRAWER_BODY */}
            <DrawerFooter>
              <div className="flex w-full items-center justify-between">
                <Button
                  onClick={() => {
                    setAdjustedFilters(form.getValues());
                  }}
                  className="text-md w-[49%] font-medium">
                  Apply
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    form.reset();
                    setAdjustedFilters(form.getValues());
                    console.log("Filters reset.");
                  }}
                  className="text-md w-[49%] font-medium">
                  Reset
                </Button>
              </div>
              <DrawerClose>
                <div className="w-full rounded-md border border-gray-800 bg-bg-tertiary p-2 text-text-light">
                  Close
                </div>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Form>
    </Drawer>
  );
};

export default OrdersFilterDrawer;
